import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { startOfDay, subMonths, endOfDay } from 'date-fns';
import Paper from '@mui/material/Paper';
import { ScanFace, UserIcon } from 'lucide-react';
import {
	DataGrid,
	DataGridSortModel,
	FilterDateRange,
	Badge,
	AccessLogChip,
	ActionCell,
	UserChip,
	CiawStatusChip,
} from '@repo/components';
import GenericDataPageHeader from 'src/components/GenericDataPageHeader';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission, sortToString, stringToSort } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { getLogs, LogType } from 'src/api/admin/logs';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { useConfig } from 'src/hooks/useConfig';

const logTypePermissions: Record<LogType, string> = {
	AccessLog: PERMISSIONS.adminAccesslogsView,
	EventLog: PERMISSIONS.adminEventlogsView,
	GroupLog: PERMISSIONS.adminGrouplogsView,
	UserLog: PERMISSIONS.adminUserlogsView,
	NotificationLog: PERMISSIONS.adminNotificationlogsView,
};

export const Route = createFileRoute('/_admin/logs/')({
	component: LogsPage,
	validateSearch: (search: Record<string, unknown>): SearchParams => ({
		tab: search.tab as LogType | undefined,
		sort: search.sort as string | undefined,
		page: search.page as number | undefined,
		from: search.from as string | undefined,
		until: search.until as string | undefined,
	}),
});

const EntityChip = ({ entity, entityUrl }: { entity: { id?: string; name?: string }; entityUrl: string }) => {
	const { name, id } = entity;
	return (
		<Badge color={'default'} component="a" href={`/${entityUrl}/${id}`} target={'_blank'}>
			{name}
		</Badge>
	);
};

const SystemChip = () => {
	return (
		<Badge color={'default'}>
			<UserIcon size={16} />
			<span className="ml-1">System</span>
		</Badge>
	);
};

const methods: Record<string, string> = {
	nfc: 'NFC',
	qr: 'QR',
	ibeacon: 'BLE / Smartphone',
};

const tabs = [
	{
		id: 'AccessLog',
		label: 'Access Logs',
		permission: PERMISSIONS.adminAccesslogsView,
	},
	{
		id: 'EventLog',
		label: 'Event Logs',
		permission: PERMISSIONS.adminEventlogsView,
	},
	{
		id: 'GroupLog',
		label: 'Company Logs',
		permission: PERMISSIONS.adminGrouplogsView,
	},
	{
		id: 'UserLog',
		label: 'User Logs',
		permission: PERMISSIONS.adminUserlogsView,
	},
	{
		id: 'NotificationLog',
		label: 'Notification Logs',
		permission: PERMISSIONS.adminNotificationlogsView,
	},
];

interface SearchParams {
	tab?: LogType;
	sort?: string;
	page?: number;
	from?: string;
	until?: string;
}

function LogsPage() {
	const { user: authenticatedUser } = useAuthenticatedUser();
	const { config } = useConfig();
	const useCheckinatwork = config?.featureFlags?.USE_CHECKINATWORK;
	const navigate = useNavigate();
	const search = Route.useSearch();
	const updateQueryParams = useUpdateQueryParams();
	const queryClient = useQueryClient();

	const defaultDateRange = useMemo(() => {
		const now = new Date();
		return {
			from: startOfDay(subMonths(now, 1)).toISOString(),
			until: endOfDay(now).toISOString(),
		};
	}, []);

	const effectiveFrom = search.from ?? defaultDateRange.from;
	const effectiveUntil = search.until ?? defaultDateRange.until;

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const availableLogTypes = useMemo(
		() =>
			(Object.keys(logTypePermissions) as LogType[]).filter((logType) =>
				verifyPermission([logTypePermissions[logType]]),
			),
		[verifyPermission],
	);

	const filteredTabs = tabs.filter((tab) => verifyPermission([tab.permission]));
	const activeTab = (search.tab || availableLogTypes[0]) as LogType;

	useBreadcrumbLevel(0, 'Logs');

	const { data, isLoading, refetch } = useQuery({
		queryKey: ['logs', activeTab, search.sort, search.page, effectiveFrom, effectiveUntil],
		queryFn: () =>
			getLogs({
				type: activeTab,
				sort: search.sort || 'timestamp:desc',
				page: search.page,
				from: effectiveFrom,
				until: effectiveUntil,
			}),
		enabled: availableLogTypes.length > 0,
	});

	// Redirect if no permissions for any log type
	useEffect(() => {
		if (!availableLogTypes.length) {
			navigate({
				to: '/403',
				search: { permissions: 'adminAccesslogsView', occurredAt: '/logs', method: 'GET', uuid: undefined },
			});
		}
	}, [availableLogTypes.length, navigate]);

	if (!availableLogTypes.length) {
		return null;
	}

	const revalidateLogs = () => {
		queryClient.invalidateQueries({ queryKey: ['logs'] });
		refetch();
	};

	const columns = [
		{
			field: 'timestamp',
			headerName: 'Date',
			width: 170,
			valueFormatter: (params: { value: string }) =>
				`${new Date(params.value).toLocaleDateString('en-GB')} ${new Date(params.value).toLocaleTimeString('en-GB')}`,
		},
		...(activeTab === 'AccessLog'
			? [
					{
						field: 'user',
						headerName: 'User',
						flex: 1,
						renderCell: (c: { value: unknown }) => c.value && <UserChip user={c.value} />,
						sortable: false,
					},
				]
			: []),
		...(['UserLog', 'EventLog', 'GroupLog'].includes(activeTab)
			? [
					{
						field: 'actor',
						headerName: 'Actor',
						flex: 1,
						renderCell: (c: { value: unknown }) => (c.value ? <UserChip user={c.value} /> : <SystemChip />),
						sortable: false,
					},
				]
			: []),
		...(activeTab === 'EventLog' || activeTab === 'GroupLog'
			? [
					{
						field: activeTab === 'EventLog' ? 'event' : 'group',
						headerName: activeTab === 'EventLog' ? 'Event' : 'Group',
						flex: 1,
						renderCell:
							activeTab === 'EventLog'
								? (c: { value: { id?: string; name?: string } }) =>
										c.value && <EntityChip entity={c.value} entityUrl="events" />
								: (c: { value: { id?: string; name?: string } }) =>
										c.value && <EntityChip entity={c.value} entityUrl="groups" />,
						sortable: false,
					},
				]
			: []),
		...(!['EventLog', 'GroupLog', 'NotificationLog', 'AccessLog'].includes(activeTab)
			? [
					{
						field: 'user',
						headerName: 'User',
						flex: 1,
						renderCell: (c: { value: unknown }) => c.value && <UserChip user={c.value} />,
						sortable: false,
					},
				]
			: []),
		...(activeTab === 'UserLog'
			? [
					{
						field: 'data.fileId',
						headerName: 'Document',
						flex: 1,
						renderCell: (c: { row?: { data?: { fileId?: string; fileName?: string }; userId?: string } }) => {
							const fileId = c.row?.data?.fileId;
							const fileName = c.row?.data?.fileName;
							const userId = c.row?.userId;
							if (!fileId || !userId) return null;
							return (
								<Badge
									variant="outline"
									component="a"
									href={`/users/${userId}/documents/${fileId}`}
									target="_blank"
									className="hover:cursor-pointer"
								>
									{fileName || 'View Document'}
								</Badge>
							);
						},
						sortable: false,
					},
				]
			: []),
		...(activeTab === 'NotificationLog'
			? [
					{
						field: 'recipients',
						headerName: 'Recipients',
						flex: 1,
						renderCell: (c: { value: unknown[] }) =>
							Array.isArray(c.value) ? c.value.map((r, i) => <UserChip key={i} user={r} />) : <></>,
						sortable: false,
					},
				]
			: []),
		...(['UserLog', 'EventLog', 'GroupLog', 'NotificationLog'].includes(activeTab)
			? [
					{
						field: 'action',
						headerName: 'Action',
						flex: 3,
						renderCell: (c: { value: unknown; row: { type?: string } }) =>
							c.value && <ActionCell action={c.value} log={c.row} logType={c.row.type} />,
						sortable: false,
					},
				]
			: []),
		...(activeTab === 'AccessLog'
			? [
					{
						field: 'zone.name',
						headerName: 'Zone',
						flex: 1,
						sortable: false,
					},
				]
			: []),
		...(activeTab === 'AccessLog'
			? [
					{
						field: 'device.name',
						headerName: 'Device',
						flex: 1,
						sortable: false,
						renderCell: (c: { row?: { data?: { deviceDescription?: string } }; value?: string }) =>
							c.row?.data?.deviceDescription || c.value || '',
					},
				]
			: []),
		...(activeTab === 'AccessLog'
			? [
					{
						field: 'data.origin',
						headerName: 'Token',
						flex: 1,
						sortable: false,
						valueFormatter: (c: { value: string }) => methods[c.value] || '',
					},
				]
			: []),
		...(activeTab === 'AccessLog'
			? [
					{
						field: 'data.faceResults.matchFound',
						headerName: 'Biometry',
						flex: 1,
						sortable: false,
						renderCell: (c: {
							value?: boolean;
							row?: { data?: { faceResults?: { mRSID?: { success?: boolean } } } };
						}) => {
							if (!c.value) return '';
							const highConfidence = c.row?.data?.faceResults?.mRSID?.success;
							const label = highConfidence ? 'High' : 'Medium';
							const variant = highConfidence ? 'success' : 'warning';
							return (
								<Badge variant={variant}>
									<ScanFace className="h-4 w-4 mr-1" />
									{label}
								</Badge>
							);
						},
					},
				]
			: []),
		...(activeTab === 'AccessLog' && useCheckinatwork
			? [
					{
						field: 'syncStatus.checkinatwork.status',
						headerName: 'CIAW Status',
						flex: 1,
						sortable: false,
						renderCell: (c: { row: any }) => (
							<CiawStatusChip
								status={c.row?.syncStatus?.checkinatwork?.status}
								remarks={c.row?.syncStatus?.checkinatwork?.remarks || []}
							/>
						),
					},
				]
			: []),
		...(activeTab === 'AccessLog' && useCheckinatwork
			? [
					{
						field: 'syncStatus.checkinatwork.presenceRegistrationId',
						headerName: 'CIAW ID',
						flex: 1,
						sortable: false,
					},
				]
			: []),
		...(activeTab === 'AccessLog'
			? [
					{
						field: 'status',
						headerName: 'Status',
						width: 200,
						sortable: false,
						renderCell: (c: { row: unknown }) => <AccessLogChip log={c.row} />,
					},
				]
			: []),
		...(activeTab === 'NotificationLog'
			? [
					{
						field: 'subject',
						headerName: 'Subject',
						flex: 1,
					},
				]
			: []),
	];

	const handleSort = ([sort]: DataGridSortModel) => {
		updateQueryParams({ sort: sortToString(sort) });
	};

	const handlePaginationModelChange = ({ page }: { page: number }) => {
		updateQueryParams({ page: page });
	};

	const handleTypeChange = (tab: string) => {
		updateQueryParams({ tab });
	};

	const handleDateFilterChange = (dateFilter: { from?: string; until?: string }) => {
		updateQueryParams(dateFilter);
	};

	return (
		<>
			<GenericDataPageHeader tabs={filteredTabs} defaultTab={filteredTabs[0]?.id} onTabChange={handleTypeChange} />
			<div className="p-4 pt-0">
				{/* Filter */}
				<div className="mt-2 flex flex-row flex-wrap gap-2">
					<div className="w-3/5 flex-grow flex gap-2">
						<FilterDateRange
							label="Filter Date Range"
							value={{
								from: effectiveFrom,
								until: effectiveUntil,
							}}
							onChange={handleDateFilterChange}
							sx={{ width: 220 }}
						/>
					</div>
				</div>
				<Paper variant="outlined" className="mt-2">
					<DataGrid
						data={data?.data || []}
						columns={columns}
						isLoading={isLoading}
						rowCount={data?.count || 0}
						paginationMode="server"
						emptyLabel="No logs were found in the selected date range. Please try a different date range."
						sortingMode="server"
						onPaginationModelChange={handlePaginationModelChange}
						onSortModelChange={handleSort}
						sortingOrder={['asc', 'desc']}
						sortModel={[stringToSort((search.sort as `${string}:${string}`) || 'timestamp:desc')]}
						pageSize={data?.pageSize || 25}
						pageSizeOptions={[data?.pageSize || 25]}
						page={data?.page || 0}
						pagination
						getRowClassName={(p: { row: { markedForReview?: boolean } }) => (p.row.markedForReview ? 'error' : '')}
						onReload={revalidateLogs}
					/>
				</Paper>
			</div>
		</>
	);
}
